import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Search, CalendarOff, Check, X, Trash2, ChevronRight, Plus, Users, Clock } from 'lucide-react';
import {
  DataTable, Person, StatusPill, Button, PageHeader, Pagination, Modal, FormField,
} from '../../components';
import leaveService, { canApproveLeave } from '../../services/leaveService';
import { useAuth } from '../../context/AuthContext';
import { LEAVE_TYPE_FILTER_OPTIONS } from '../../utils/leaveTypes';
import { quantityLabel } from '../leave/leaveUnits';

/**
 * Leave & Approval — every leave request in the organisation.
 *
 * Access is granted by the admin assigning the "Leave & Approval" menu, like
 * any other module — not by role. The gate is checked twice on purpose: here,
 * so someone who types the URL without the menu gets a clean message instead
 * of an error page, and again in the API, which is what actually protects the
 * data.
 *
 * Approving a request is the moment it becomes real: until then its `pending`
 * status keeps it out of every rota and availability query, so nothing on the
 * schedule moves while a manager is still deciding.
 */

const STATUS_TONE = {
  pending: 'warn',
  approved: 'good',
  rejected: 'crit',
  cancelled: 'mute',
};

/**
 * The left-hand nav, in two groups.
 *
 * STATUS_TABS filter the same list of requests, so each carries its count and
 * they read as one set. VIEW_TABS are different questions entirely and answer
 * with a different table, so they sit under a rule with an icon apiece rather
 * than pretending to be a fifth and sixth filter.
 */
const STATUS_TABS = [
  { key: 'pending', label: 'Awaiting approval', count: 'pending' },
  { key: 'approved', label: 'Approved', count: 'approved' },
  { key: 'rejected', label: 'Rejected', count: 'rejected' },
  { key: 'all', label: 'All requests', count: 'total' },
];

const VIEW_TABS = [
  // Not a status filter — this swaps the request list for the per-staff totals
  // view. Same question asked a different way: "who is off, and how much?"
  { key: 'summary', label: 'Staff summary', icon: Users },
  // Nor is this one: each person's annual leave ALLOWANCE and what's left of
  // it. Distinct from Staff summary, which is leave already taken — this is
  // what they are still owed, and the only place it can be changed.
  { key: 'allowances', label: 'Allowances', icon: Clock },
];

/**
 * How each request shape reads in a list. 'hours_total' is named separately
 * from 'hours' because they cost different amounts of allowance: one is part of
 * a single shift, the other a block of hours that may span several. Rows written
 * before duration_type existed fall through to full day(s).
 *
 * 'hours_total' is READ-ONLY here now: Add leave no longer offers it, but rows
 * already carrying it — and any still arriving from the staff request forms —
 * must keep reading correctly.
 */
const DURATION_LABELS = {
  hours: 'part of a shift',
  hours_total: 'hours across shifts',
  full_day: 'full day(s)',
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_OPTIONS = [
  { value: '', label: 'Whole year' },
  ...MONTH_NAMES.map((m, i) => ({ value: String(i + 1), label: m })),
];

/** The current year and the four before it — enough for any real lookback. */
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const y = new Date().getFullYear() - i;
  return { value: String(y), label: String(y) };
});

// One list for every screen that names an absence — see utils/leaveTypes.js.
const TYPE_OPTIONS = LEAVE_TYPE_FILTER_OPTIONS;

const CARD = 'bg-white dark:bg-[#262b33] border border-[#E2E8F0] dark:border-gray-800 rounded-2xl shadow-sm';
const LIMIT = 10;

const LeaveApprovals = () => {
  const { allAvailableMenus, menuIds } = useAuth();
  const allowed = canApproveLeave(allAvailableMenus, menuIds);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [tab, setTab] = useState('pending');
  const [employeeId, setEmployeeId] = useState('');
  const [leaveType, setLeaveType] = useState('');
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');

  // { row, action } — one modal serves approve, reject and remove.
  const [review, setReview] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Staff summary tab
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsYear, setStatsYear] = useState(String(new Date().getFullYear()));
  const [statsMonth, setStatsMonth] = useState(String(new Date().getMonth() + 1));
  // The staff row opened from the summary table — their individual leaves.
  const [detail, setDetail] = useState(null);

  // "Add leave" — a manager recording leave on somebody's behalf.
  const [addOpen, setAddOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addForm, setAddForm] = useState(null);

  // Allowances tab — each person's entitlement and what's left of it.
  const [allowances, setAllowances] = useState(null);
  const [allowancesLoading, setAllowancesLoading] = useState(false);
  const [allowanceYear, setAllowanceYear] = useState(String(new Date().getFullYear()));
  // The row being edited: { employee_id, employee_name, days, notes }.
  const [editAllowance, setEditAllowance] = useState(null);
  const [savingAllowance, setSavingAllowance] = useState(false);

  const isSummary = tab === 'summary';
  const isAllowances = tab === 'allowances';

  // The heading above the table. Falls back to the first entry so an unknown
  // tab in the URL cannot render a card with no title.
  const activeTab = [...STATUS_TABS, ...VIEW_TABS].find((t) => t.key === tab) || STATUS_TABS[0];
  // Only the status filters count something. Staff summary and Allowances are
  // lists of people, not of requests, so a request count beside them would be
  // answering a question nobody asked.
  const activeCount = activeTab.count ? (summary[activeTab.count] ?? 0) : null;

  const fetchRequests = useCallback(async (targetPage = 1) => {
    // The summary and allowances tabs have their own loaders — don't page the
    // request list for either of them.
    if (!allowed || isSummary || isAllowances) return;
    try {
      setLoading(true);
      const res = await leaveService.getRequests({
        status: tab,
        employee_id: employeeId || undefined,
        leave_type: leaveType || undefined,
        search: submittedSearch || undefined,
        page: targetPage,
        limit: LIMIT,
      });
      if (res?.success) {
        setRows(res.data || []);
        setSummary(res.summary || {});
        setTotalPages(res.pagination?.pages || 1);
        setPage(res.pagination?.page || 1);
      } else {
        toast.error(res?.message || 'Could not load leave requests');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load leave requests');
    } finally {
      setLoading(false);
    }
  }, [allowed, isSummary, isAllowances, tab, employeeId, leaveType, submittedSearch]);

  useEffect(() => { fetchRequests(page); }, [page, fetchRequests]);

  const fetchStats = useCallback(async () => {
    if (!allowed || !isSummary) return;
    try {
      setStatsLoading(true);
      const res = await leaveService.getStats(statsYear, statsMonth || null);
      if (res?.success) setStats(res.data);
      else toast.error(res?.message || 'Could not load the leave summary');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load the leave summary');
    } finally {
      setStatsLoading(false);
    }
  }, [allowed, isSummary, statsYear, statsMonth]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const fetchAllowances = useCallback(async () => {
    if (!allowed || !isAllowances) return;
    try {
      setAllowancesLoading(true);
      const res = await leaveService.getEntitlements(allowanceYear);
      if (res?.success) setAllowances(res.data);
      else toast.error(res?.message || 'Could not load leave allowances');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not load leave allowances');
    } finally {
      setAllowancesLoading(false);
    }
  }, [allowed, isAllowances, allowanceYear]);

  useEffect(() => { fetchAllowances(); }, [fetchAllowances]);

  // Any filter change invalidates the current page number.
  useEffect(() => { setPage(1); }, [tab, employeeId, leaveType, submittedSearch]);

  useEffect(() => {
    if (!allowed) return;
    leaveService.getEmployees()
      .then((res) => setEmployees(res?.data || []))
      .catch(() => { /* the filter is a convenience; a failure isn't worth a toast */ });
  }, [allowed]);

  if (!allowed) {
    return (
      <div className="w-full space-y-6 pb-8">
        <PageHeader title="Leave & Approval" />
        <div className={`${CARD} p-8 text-center`}>
          <p className="text-[14px] text-[#64748B] dark:text-gray-400">
            You don't have access to leave approvals — ask your administrator to grant you the
            {' '}<span className="font-semibold">Leave &amp; Approval</span> menu. Your own requests are
            always available under <span className="font-semibold">Apply for leave</span> in the account menu.
          </p>
        </div>
      </div>
    );
  }

  const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const openAdd = () => {
    setAddForm({
      employee_id: '',
      leave_type: 'sick',
      duration_type: 'full_day',
      start_date: todayStr(),
      end_date: todayStr(),
      start_time: '09:00',
      end_time: '13:00',
      notes: '',
      status: 'approved',
    });
    setAddOpen(true);
  };

  const setAdd = (name, value) => setAddForm((f) => ({ ...f, [name]: value }));
  const onAddChange = (e) => setAdd(e.target.name, e.target.value);

  const submitAdd = async (e) => {
    e.preventDefault();
    const f = addForm;
    if (!f.employee_id) return toast.error('Choose the staff member.');
    if (f.duration_type === 'hours') {
      if (!f.start_date) return toast.error('Pick the date.');
      if (f.end_time <= f.start_time) return toast.error('The end time must be after the start time.');
    } else if (f.start_date > f.end_date) {
      return toast.error('The start date cannot be after the end date.');
    }
    if (f.leave_type === 'other' && !f.notes.trim()) {
      return toast.error('Please add a note explaining the reason.');
    }

    const common = {
      employee_id: f.employee_id,
      leave_type: f.leave_type,
      notes: f.notes,
      status: f.status,
    };

    let payload;
    if (f.duration_type === 'hours') {
      payload = {
        ...common,
        duration_type: 'hours',
        start_date: f.start_date,
        start_time: f.start_time,
        end_time: f.end_time,
      };
    } else {
      payload = {
        ...common,
        duration_type: 'full_day',
        start_date: f.start_date,
        end_date: f.end_date,
      };
    }

    try {
      setAddSaving(true);
      const res = await leaveService.addLeaveForEmployee(payload);
      if (res?.success) {
        toast.success(res.message || 'Leave added');
        setAddOpen(false);
        // Land on the tab the new row is actually in, so it's visible.
        setTab(f.status === 'approved' ? 'approved' : 'pending');
        fetchRequests(1);
        if (isSummary) fetchStats();
        // The allowance table needs no nudge here: adding leave moves the view
        // to the Approved or Pending tab, and fetchAllowances re-runs whenever
        // the Allowances tab is selected again.
      } else {
        toast.error(res?.message || 'Could not add the leave');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not add the leave');
    } finally {
      setAddSaving(false);
    }
  };

  const openReview = (row, action) => {
    setReview({ row, action });
    setReviewNote('');
  };

  const submitReview = async () => {
    if (!review) return;
    const { row, action } = review;

    if (action === 'reject' && !reviewNote.trim()) {
      toast.error('Please give a reason — the requester will see it.');
      return;
    }

    try {
      setSaving(true);
      const res = action === 'remove'
        ? await leaveService.removeRequest(row.id)
        : await leaveService.reviewRequest(row.id, action, reviewNote);

      if (res?.success) {
        toast.success(res.message || 'Leave updated');
        setReview(null);
        fetchRequests(page);
      } else {
        toast.error(res?.message || 'Could not update the request');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not update the request');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      key: 'employee',
      header: 'Staff member',
      render: (r) => <Person name={r.employee_name || '—'} sub={r.job_title || r.employee_code || ''} />,
    },
    {
      key: 'type',
      header: 'Type',
      render: (r) => (
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-gray-700 dark:text-gray-300">{r.leave_type_label}</div>
          {r.notes && (
            <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 truncate max-w-[240px]" title={r.notes}>
              {r.notes}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'period',
      header: 'When',
      render: (r) => (
        <div>
          <div className="text-[13px] text-gray-700 dark:text-gray-300">{r.period_label}</div>
          <div className="text-[12px] text-[#94A3B8] dark:text-gray-500">
            {r.hours != null ? `${r.hours}h` : ''}
            {` · ${DURATION_LABELS[r.duration_type] || 'full day(s)'}`}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <div className="flex flex-col items-start gap-1">
          <StatusPill tone={STATUS_TONE[r.status] || 'mute'}>{r.status_label}</StatusPill>
          {r.reviewer_name && (
            <span className="text-[11.5px] text-[#94A3B8] dark:text-gray-500">by {r.reviewer_name}</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          {r.status === 'pending' && (
            <>
              <Button
                variant="success"
                size="sm"
                leftIcon={<Check className="w-3.5 h-3.5" />}
                onClick={() => openReview(r, 'approve')}
              >
                Approve
              </Button>
              <Button
                variant="danger-soft"
                size="sm"
                leftIcon={<X className="w-3.5 h-3.5" />}
                onClick={() => openReview(r, 'reject')}
              >
                Reject
              </Button>
            </>
          )}
          {r.status === 'approved' && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 className="w-3.5 h-3.5" />}
              onClick={() => openReview(r, 'remove')}
            >
              Remove
            </Button>
          )}
        </div>
      ),
    },
  ];

  const tile = (label, value, tone) => (
    <div className={`${CARD} p-4`}>
      <div className={`text-[22px] font-extrabold ${tone}`}>{value ?? 0}</div>
      <div className="text-[12px] text-[#64748B] dark:text-gray-400 mt-0.5">{label}</div>
    </div>
  );

  const hrs = (n) => `${Number(n || 0)}h`;

  // Per-staff totals for the summary tab. Same DataTable, different question.
  const statsColumns = [
    {
      key: 'employee',
      header: 'Staff member',
      render: (r) => (
        <Person
          name={r.is_self ? `${r.employee_name} (You)` : r.employee_name}
          sub={r.job_title || r.employee_code || ''}
        />
      ),
    },
    {
      key: 'sick',
      header: 'Sick',
      render: (r) => <span className="text-[13px] text-rose-600 dark:text-rose-400">{hrs(r.sick_hours)}</span>,
    },
    {
      key: 'annual',
      header: 'Annual',
      render: (r) => <span className="text-[13px] text-blue-600 dark:text-blue-400">{hrs(r.annual_hours)}</span>,
    },
    {
      key: 'other',
      header: 'Other',
      render: (r) => <span className="text-[13px] text-gray-600 dark:text-gray-400">{hrs(r.other_hours)}</span>,
    },
    {
      key: 'total',
      header: 'Total',
      render: (r) => (
        <span className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">{hrs(r.total_hours)}</span>
      ),
    },
    {
      key: 'days',
      header: 'Full days',
      render: (r) => <span className="text-[13px] text-gray-600 dark:text-gray-400">{r.total_days || 0}</span>,
    },
    {
      key: 'requests',
      header: 'Requests',
      align: 'right',
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <span className="text-[13px] text-gray-600 dark:text-gray-400">{r.total_requests || 0}</span>
          <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
        </div>
      ),
    },
  ];

  // The 12-month strip. Bars are scaled to the busiest month rather than an
  // absolute scale, so a quiet year still shows its own shape.
  const renderMonthStrip = () => {
    const months = stats?.months || [];
    const peak = Math.max(...months.map((m) => m.total_hours || 0), 1);

    return (
      <div className="p-4 border-b border-[#E2E8F0] dark:border-gray-800">
        <p className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mb-3">
          Leave hours across {stats?.year} — whole organisation
        </p>
        <div className="flex items-end gap-1.5 h-[90px]">
          {months.map((m) => {
            const height = Math.round(((m.total_hours || 0) / peak) * 100);
            const selected = String(m.month) === String(statsMonth);
            return (
              <button
                key={m.month}
                type="button"
                // Clicking a bar filters the table to that month — the fastest
                // route from "that month looks heavy" to "who was off?".
                onClick={() => setStatsMonth(selected ? '' : String(m.month))}
                title={`${MONTH_NAMES[m.month - 1]}: ${hrs(m.total_hours)} across ${m.total_requests} request(s)`}
                className="flex-1 flex flex-col items-center justify-end h-full gap-1.5 group"
              >
                <div className="w-full flex-1 flex items-end">
                  <div
                    style={{ height: `${Math.max(height, 2)}%` }}
                    className={`w-full rounded-t transition-colors ${selected
                      ? 'bg-blue-600'
                      : 'bg-blue-200 dark:bg-blue-900/50 group-hover:bg-blue-400'
                      }`}
                  />
                </div>
                <span className={`text-[10px] font-semibold ${selected
                  ? 'text-blue-600'
                  : 'text-[#94A3B8] dark:text-gray-500'
                  }`}
                >
                  {MONTH_NAMES[m.month - 1].substring(0, 3)}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /**
   * Save one person's allowance.
   *
   * The API is what enforces the floor (you cannot set an allowance below what
   * somebody has already used or booked) — its message names the figure and the
   * remedy, so it is shown as-is rather than second-guessed here.
   */
  const submitAllowance = async (e) => {
    e.preventDefault();
    const target = editAllowance;
    const days = Number(target.days);
    if (!Number.isFinite(days) || days < 0) {
      return toast.error('Enter the allowance as a number of days.');
    }

    try {
      setSavingAllowance(true);
      const res = await leaveService.updateEntitlement(target.employee_id, {
        year: Number(allowanceYear),
        entitlement_days: days,
        notes: target.notes,
      });
      if (res?.success) {
        toast.success(res.message || 'Allowance updated');
        setEditAllowance(null);
        fetchAllowances();
      } else {
        toast.error(res?.message || 'Could not update the allowance');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not update the allowance');
    } finally {
      setSavingAllowance(false);
    }
    return undefined;
  };

  const allowanceUnit = allowances?.unit || 'days';

  const allowanceColumns = [
    {
      key: 'employee',
      header: 'Staff member',
      render: (r) => (
        <Person
          name={`${r.employee_name}${r.is_self ? ' (You)' : ''}`}
          subtitle={r.job_title || r.employee_code}
        />
      ),
    },
    {
      key: 'entitlement',
      header: 'Allowance',
      render: (r) => (
        <div>
          <span className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100">
            {quantityLabel(r.entitlement_days, r.day_hours, allowanceUnit)}
          </span>
          {/* Nobody has set this person's allowance, so they are on the
              organisation default. Worth distinguishing from a figure a manager
              actually chose. */}
          {r.is_default && (
            <div className="text-[11.5px] text-[#94A3B8] dark:text-gray-500">org default</div>
          )}
          {r.entitlement_notes && (
            <div className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 max-w-[200px] truncate" title={r.entitlement_notes}>
              {r.entitlement_notes}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'used',
      header: 'Taken',
      render: (r) => (
        <span className="text-[13px] text-gray-700 dark:text-gray-300">
          {quantityLabel(r.used_days, r.day_hours, allowanceUnit)}
        </span>
      ),
    },
    {
      key: 'pending',
      header: 'Awaiting approval',
      render: (r) => (
        <span className={`text-[13px] ${r.pending_days > 0
          ? 'font-semibold text-amber-600 dark:text-amber-400'
          : 'text-gray-500 dark:text-gray-400'}`}
        >
          {quantityLabel(r.pending_days, r.day_hours, allowanceUnit)}
        </span>
      ),
    },
    {
      key: 'remaining',
      header: 'Remaining',
      render: (r) => (
        <span className={`text-[13.5px] font-bold ${r.remaining_days < 0
          ? 'text-rose-500'
          : 'text-emerald-600 dark:text-emerald-400'}`}
        >
          {quantityLabel(r.remaining_days, r.day_hours, allowanceUnit)}
        </span>
      ),
    },
    {
      key: 'shift',
      header: 'Shift length',
      render: (r) => (
        // The conversion rate for this person, and a warning when there isn't
        // one. A missing rota pattern is the one reason a balance can be wrong
        // for a cause that has nothing to do with leave, so it is named here
        // rather than left to be puzzled over.
        r.has_roster ? (
          <span className="text-[12.5px] text-gray-600 dark:text-gray-400">{r.day_hours}h</span>
        ) : (
          <span className="text-[12px] text-amber-600 dark:text-amber-400" title="No rota pattern on file — leave is counted at an assumed 8-hour day.">
            8h assumed
          </span>
        )
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditAllowance({
            employee_id: r.employee_id,
            employee_name: r.employee_name,
            // Always edited in DAYS, whatever unit the organisation displays.
            // An allowance is stored in days, and letting it be typed in hours
            // would mean rounding somebody's entitlement through a division.
            days: String(r.entitlement_days),
            notes: r.entitlement_notes || '',
            balance: r,
          })}
        >
          Edit
        </Button>
      ),
    },
  ];

  const reviewCopy = {
    approve: {
      title: 'Approve leave',
      body: 'They will be marked unavailable on the rota for this period and notified straight away.',
      confirm: 'Approve',
      variant: 'primary',
      noteLabel: 'Note (optional)',
    },
    reject: {
      title: 'Reject leave',
      body: 'They stay on the rota as normal. Your reason is shown to them, so please be clear.',
      confirm: 'Reject request',
      variant: 'danger',
      noteLabel: 'Reason (required)',
    },
    remove: {
      title: 'Remove leave',
      body: 'This deletes the approved absence and puts them back on the rota for those dates.',
      confirm: 'Remove leave',
      variant: 'danger',
      noteLabel: null,
    },
  }[review?.action || 'approve'];

  return (
    // Matches My leave: `space-y-6` keeps the header clear of the tiles, `pb-8`
    // is bottom breathing room.
    <div className="w-full space-y-6 pb-8">
      <PageHeader
        title="Leave & Approval"
        subtitle="Review and approve time off across your organisation"
        actions={(
          <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={openAdd}>
            Add leave
          </Button>
        )}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {tile('Awaiting approval', summary.pending, 'text-amber-500')}
        {tile('Approved', summary.approved, 'text-emerald-600')}
        {tile('Rejected', summary.rejected, 'text-rose-500')}
        {tile('Total requests', summary.total, 'text-blue-600')}
      </div>

      {/* Nav beside the list rather than above it. Six entries in a pill strip
          wrapped to two rows on a laptop and read as one long sentence; down
          the side each is a fixed target with its count beside it, and the
          heading on the right says which one you are looking at. Stacks above
          the content below lg, where there is no width to spare. */}
      <div className="flex flex-col lg:flex-row gap-5 items-start">

        <nav className={`${CARD} w-full lg:w-[240px] lg:shrink-0 py-2 overflow-hidden`} aria-label="Leave views">
          {STATUS_TABS.map((t) => {
            const active = tab === t.key;
            const count = summary[t.count];
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                aria-current={active ? 'page' : undefined}
                className={`relative w-full flex items-center justify-between gap-3 pl-5 pr-4 py-3 text-[13.5px] font-semibold text-left transition ${active
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/25 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                  }`}
              >
                {/* Flush to the card's edge and the full height of the row, so
                    the marker and its fill read as one selected band. */}
                {active && (
                  <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-blue-600 dark:bg-blue-400" aria-hidden="true" />
                )}
                <span className="truncate">{t.label}</span>
                <span className={`text-[12.5px] font-bold tabular-nums shrink-0 ${active
                  ? 'text-blue-700 dark:text-blue-300'
                  : 'text-gray-400 dark:text-gray-500'}`}
                >
                  {count ?? 0}
                </span>
              </button>
            );
          })}

          <div className="my-2 mx-5 border-t border-[#E2E8F0] dark:border-gray-800" />

          {VIEW_TABS.map((t) => {
            const active = tab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                aria-current={active ? 'page' : undefined}
                className={`relative w-full flex items-center gap-2.5 pl-5 pr-4 py-3 text-[13.5px] font-semibold text-left transition ${active
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/25 dark:text-blue-300'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]'
                  }`}
              >
                {active && (
                  <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-blue-600 dark:bg-blue-400" aria-hidden="true" />
                )}
                <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                <span className="truncate">{t.label}</span>
              </button>
            );
          })}
        </nav>

        <div className={`${CARD} flex-1 min-w-0 w-full overflow-hidden`}>
        {/* Which view you are in. The sidebar says it too, but the count belongs
            beside the heading of the thing being counted. */}
        <div className="flex items-center gap-2.5 px-5 pt-5 pb-4">
          <h2 className="text-[17px] font-bold text-[#0B1220] dark:text-gray-100">{activeTab.label}</h2>
          {activeCount !== null && (
            <span className="text-[12px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/25 dark:text-blue-300 tabular-nums">
              {activeCount}
            </span>
          )}
        </div>

        {isAllowances ? (
          <>
            <div className="flex flex-wrap items-center gap-3 px-5 pb-4">
              <p className="flex-1 min-w-[240px] text-[13px] text-[#64748B] dark:text-gray-400">
                Each person's annual leave allowance for {allowanceYear} and what is left of it.
                Only annual leave is deducted — sickness and other leave are recorded but
                don't come off the allowance.
                {allowances?.default_leave_days != null && (
                  <>
                    {' '}New staff start on{' '}
                    <span className="font-semibold text-gray-700 dark:text-gray-200">
                      {allowances.default_leave_days} days
                    </span>
                    , set in Organisation settings.
                  </>
                )}
              </p>
              <div className="w-[120px]">
                <FormField
                  name="allowance_year"
                  type="select"
                  value={allowanceYear}
                  onChange={(e) => setAllowanceYear(e.target.value)}
                  options={YEAR_OPTIONS}
                />
              </div>
            </div>

            <DataTable
              bare
              columns={allowanceColumns}
              data={allowances?.employees || []}
              loading={allowancesLoading}
              rowKey={(r) => r.employee_id}
              emptyIcon={CalendarOff}
              emptyTitle="No staff to show"
              emptyText="Allowances appear here once your organisation has active staff."
            />
          </>
        ) : isSummary ? (
          <>
            <div className="flex flex-wrap items-center gap-3 px-5 pb-4">
              <p className="flex-1 min-w-[200px] text-[13px] text-[#64748B] dark:text-gray-400">
                Approved leave taken per staff member
                {statsMonth ? ` in ${MONTH_NAMES[Number(statsMonth) - 1]} ${statsYear}` : ` across ${statsYear}`}.
              </p>
              <div className="w-[150px]">
                <FormField
                  name="stats_month"
                  type="select"
                  value={statsMonth}
                  onChange={(e) => setStatsMonth(e.target.value)}
                  options={MONTH_OPTIONS}
                />
              </div>
              <div className="w-[110px]">
                <FormField
                  name="stats_year"
                  type="select"
                  value={statsYear}
                  onChange={(e) => setStatsYear(e.target.value)}
                  options={YEAR_OPTIONS}
                />
              </div>
            </div>

            {stats && renderMonthStrip()}

            {stats?.totals && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#E2E8F0] dark:bg-gray-800 border-b border-[#E2E8F0] dark:border-gray-800">
                {[
                  ['Total hours', hrs(stats.totals.total_hours)],
                  ['Sick', hrs(stats.totals.sick_hours)],
                  ['Annual Leave', hrs(stats.totals.annual_hours)],
                  ['Other', hrs(stats.totals.other_hours)],
                  ['Staff who took leave', stats.totals.staff_count],
                ].map(([label, value]) => (
                  <div key={label} className="bg-white dark:bg-[#262b33] p-3.5">
                    <div className="text-[17px] font-extrabold text-[#0B1220] dark:text-gray-100">{value}</div>
                    <div className="text-[11.5px] text-[#64748B] dark:text-gray-400 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
            )}

            <DataTable
              bare
              columns={statsColumns}
              data={stats?.staff || []}
              loading={statsLoading}
              rowKey={(r) => r.employee_id}
              onRowClick={(r) => setDetail(r)}
              emptyIcon={CalendarOff}
              emptyTitle="No leave taken"
              emptyText={statsMonth
                ? `Nobody took approved leave in ${MONTH_NAMES[Number(statsMonth) - 1]} ${statsYear}.`
                : `Nobody took approved leave in ${statsYear}.`}
            />
          </>
        ) : (
          <>
        {/* No rule under this row: the table's own header band already
            separates it, and two lines together read as an empty strip. */}
        <div className="flex flex-wrap items-center gap-3 px-5 pb-4">
          <form
            className="relative flex-1 min-w-[200px]"
            onSubmit={(e) => { e.preventDefault(); setSubmittedSearch(search.trim()); }}
          >
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by staff name…"
              className="w-full pl-9 pr-3 py-2.5 text-[13px] rounded-lg border border-gray-200 dark:border-gray-800 bg-transparent text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:border-blue-500"
            />
          </form>

          <div className="w-[190px]">
            <FormField
              name="employee_id"
              type="select"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              placeholder="All staff"
              options={[
                { value: '', label: 'All staff' },
                // A manager takes leave too, so they're in their own staff
                // list — marked so their own name doesn't read as a colleague's.
                ...employees.map((e) => ({
                  value: String(e.id),
                  label: e.is_self ? `${e.name} (You)` : e.name,
                })),
              ]}
            />
          </div>

          <div className="w-[170px]">
            <FormField
              name="leave_type"
              type="select"
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value)}
              placeholder="All types"
              options={TYPE_OPTIONS}
            />
          </div>
        </div>

        <DataTable
          bare
          columns={columns}
          data={rows}
          loading={loading}
          rowKey={(r) => r.id}
          emptyIcon={CalendarOff}
          emptyTitle={tab === 'pending' ? 'Nothing awaiting approval' : 'No leave requests'}
          emptyText={tab === 'pending'
            ? "You're all caught up — new requests will appear here."
            : 'No requests match these filters.'}
        />

        {totalPages > 1 && (
          <div className="p-4 border-t border-[#E2E8F0] dark:border-gray-800">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
          </>
        )}
        </div>
      </div>

      {/* Add leave — a manager recording leave for a staff member. Created
          APPROVED by default: a manager entering leave is deciding it, not
          asking for it. */}
      <Modal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add leave for a staff member"
        maxWidth="max-w-[560px]"
      >
        {addForm && (
          <form onSubmit={submitAdd} className="space-y-5">
            <FormField
              label="Staff member"
              name="employee_id"
              type="select"
              value={addForm.employee_id}
              onChange={onAddChange}
              placeholder="Choose a staff member"
              options={employees.map((e) => ({
                value: String(e.id),
                label: e.is_self ? `${e.name} (You)` : e.name,
              }))}
              required
            />

            <div>
              <p className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 mb-2">
                How much time?
              </p>
              <div className="flex gap-2">
                {/* Two shapes, not three. A manager entering leave for someone
                    else knows either the dates they are off or the part of a
                    shift they are missing; the third option — a bare quantity
                    of hours spread over however many shifts it covered — is
                    still stored and still renders on old rows, but nothing is
                    offered it any more. */}
                {[
                  ['hours', 'Part of a shift'],
                  ['full_day', 'Full day(s)'],
                ].map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAdd('duration_type', v)}
                    className={`flex-1 px-3 py-2.5 rounded-lg text-[13px] font-semibold transition border ${addForm.duration_type === v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-[#31373f] text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#3a4048]'
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <FormField
              label="Leave type"
              name="leave_type"
              type="select"
              value={addForm.leave_type}
              onChange={onAddChange}
              options={TYPE_OPTIONS.filter((t) => t.value)}
              required
            />

            {addForm.duration_type === 'hours' ? (
              <>
                <FormField
                  label="Date" name="start_date" type="date"
                  value={addForm.start_date} onChange={onAddChange} required
                />
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label="From" name="start_time" type="time"
                    value={addForm.start_time} onChange={onAddChange} required
                  />
                  <FormField
                    label="To" name="end_time" type="time"
                    value={addForm.end_time} onChange={onAddChange} required
                  />
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  label="Start date" name="start_date" type="date"
                  value={addForm.start_date}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAddForm((f) => ({ ...f, start_date: v, end_date: f.end_date < v ? v : f.end_date }));
                  }}
                  required
                />
                <FormField
                  label="End date" name="end_date" type="date"
                  value={addForm.end_date} onChange={onAddChange}
                  min={addForm.start_date} required
                />
              </div>
            )}

            <FormField
              label={addForm.leave_type === 'other' ? 'Reason (required)' : 'Notes (optional)'}
              name="notes" type="textarea" rows={3}
              value={addForm.notes} onChange={onAddChange}
              placeholder="e.g. rang in sick this morning"
              required={addForm.leave_type === 'other'}
            />

            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-blue-600"
                checked={addForm.status === 'pending'}
                onChange={(e) => setAdd('status', e.target.checked ? 'pending' : 'approved')}
              />
              <span className="text-[12.5px] text-[#64748B] dark:text-gray-400">
                Raise as a pending request instead of approving it now.
                <span className="block text-[11.5px] text-[#94A3B8] dark:text-gray-500">
                  Leave this unticked and the absence applies to the rota straight away.
                </span>
              </span>
            </label>

            <div className="flex items-center justify-end gap-2 pt-4 border-t border-[#F1F5F9] dark:border-gray-800">
              <Button type="button" variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" variant="primary" loading={addSaving}>
                {addSaving ? 'Saving…' : (addForm.status === 'pending' ? 'Raise request' : 'Add leave')}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Drill-down: one person's leave for the selected period, so "8 hours in
          August" can be opened to see what those 8 hours actually were. */}
      <Modal
        isOpen={!!detail}
        onClose={() => setDetail(null)}
        title={detail ? `${detail.employee_name}${detail.is_self ? ' (You)' : ''}` : ''}
        maxWidth="max-w-[620px]"
      >
        {detail && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-[#64748B] dark:text-gray-400">
              <span>
                {statsMonth ? `${MONTH_NAMES[Number(statsMonth) - 1]} ${statsYear}` : statsYear}
              </span>
              <span>·</span>
              <span><span className="font-bold text-gray-700 dark:text-gray-200">{hrs(detail.total_hours)}</span> total</span>
              <span>·</span>
              <span>{detail.total_requests} request{detail.total_requests === 1 ? '' : 's'}</span>
              {detail.total_days > 0 && (
                <>
                  <span>·</span>
                  <span>{detail.total_days} full day{detail.total_days === 1 ? '' : 's'}</span>
                </>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[
                ['Sick', detail.sick_hours, 'text-rose-600 dark:text-rose-400'],
                ['Annual Leave', detail.annual_hours, 'text-blue-600 dark:text-blue-400'],
                ['Other', detail.other_hours, 'text-gray-600 dark:text-gray-400'],
              ].map(([label, value, tone]) => (
                <div key={label} className="rounded-xl bg-[#F8FAFC] dark:bg-[#31373f] p-3">
                  <div className={`text-[16px] font-extrabold ${tone}`}>{hrs(value)}</div>
                  <div className="text-[11.5px] text-[#64748B] dark:text-gray-400">{label}</div>
                </div>
              ))}
            </div>

            <div className="max-h-[340px] overflow-y-auto -mx-1 px-1 space-y-2">
              {(detail.leaves || []).map((l) => (
                <div
                  key={l.id}
                  className="rounded-xl border border-[#E2E8F0] dark:border-gray-800 p-3.5"
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">
                          {l.leave_type_label}
                        </span>
                        <StatusPill tone={STATUS_TONE[l.status] || 'mute'}>{l.status_label}</StatusPill>
                      </div>
                      <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-1">
                        {l.period_label}
                        {l.hours != null ? ` · ${l.hours}h` : ''}
                        {` · ${DURATION_LABELS[l.duration_type] || 'full day(s)'}`}
                      </div>
                      {l.notes && (
                        <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-1.5 italic">
                          “{l.notes}”
                        </p>
                      )}
                      {l.review_note && (
                        <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 mt-1.5">
                          {l.reviewer_name ? `${l.reviewer_name}: ` : ''}{l.review_note}
                        </p>
                      )}
                    </div>
                    <div className="text-[11.5px] text-[#94A3B8] dark:text-gray-500 whitespace-nowrap text-right">
                      {/* When they asked, as opposed to when the leave is for —
                          a sickness logged the same morning reads differently
                          from one booked a month ahead. */}
                      {l.created_at ? `asked ${String(l.created_at).substring(0, 10)}` : ''}
                    </div>
                  </div>
                </div>
              ))}
              {(!detail.leaves || detail.leaves.length === 0) && (
                <p className="text-[13px] text-[#94A3B8] dark:text-gray-500 text-center py-6">
                  No leave in this period.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#F1F5F9] dark:border-gray-800">
              <Button
                variant="ghost"
                onClick={() => {
                  // Straight from "they were off a lot" to their full history.
                  setDetail(null);
                  setTab('all');
                  setEmployeeId(String(detail.employee_id));
                }}
              >
                See all their requests
              </Button>
              <Button variant="secondary" onClick={() => setDetail(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!review}
        onClose={() => setReview(null)}
        title={reviewCopy.title}
        maxWidth="max-w-[460px]"
      >
        {review && (
          <div className="space-y-4">
            <div className="rounded-xl bg-[#F8FAFC] dark:bg-[#31373f] p-3.5">
              <div className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100">
                {review.row.employee_name}
              </div>
              <div className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-0.5">
                {review.row.leave_type_label} · {review.row.period_label}
                {review.row.hours != null ? ` · ${review.row.hours}h` : ''}
              </div>
              {review.row.notes && (
                <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 mt-2 italic">
                  “{review.row.notes}”
                </p>
              )}
            </div>

            <p className="text-[13.5px] text-[#64748B] dark:text-gray-400">{reviewCopy.body}</p>

            {reviewCopy.noteLabel && (
              <FormField
                label={reviewCopy.noteLabel}
                name="review_note"
                type="textarea"
                rows={3}
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder={review.action === 'reject'
                  ? 'e.g. we already have two carers off that week'
                  : 'Anything you want them to know'}
              />
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setReview(null)}>Cancel</Button>
              <Button variant={reviewCopy.variant} loading={saving} onClick={submitReview}>
                {saving ? 'Saving…' : reviewCopy.confirm}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!editAllowance}
        onClose={() => setEditAllowance(null)}
        title={`${editAllowance?.employee_name || ''} — ${allowanceYear} allowance`}
        maxWidth="max-w-[460px]"
      >
        {editAllowance && (
          <form onSubmit={submitAllowance} className="space-y-4">
            {/* What they've already committed, because it is the floor the API
                will refuse to go below — better shown than discovered. */}
            <div className="grid grid-cols-3 gap-px bg-[#E2E8F0] dark:bg-gray-800 rounded-lg overflow-hidden border border-[#E2E8F0] dark:border-gray-800">
              {[
                ['Taken', editAllowance.balance.used_days],
                ['Awaiting', editAllowance.balance.pending_days],
                ['Remaining', editAllowance.balance.remaining_days],
              ].map(([label, value]) => (
                <div key={label} className="bg-white dark:bg-[#262b33] p-3">
                  <div className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">
                    {quantityLabel(value, editAllowance.balance.day_hours, allowanceUnit)}
                  </div>
                  <div className="text-[11.5px] text-[#64748B] dark:text-gray-400">{label}</div>
                </div>
              ))}
            </div>

            <FormField
              label="Allowance (days)"
              name="entitlement_days"
              type="number"
              min="0"
              step="0.5"
              value={editAllowance.days}
              onChange={(e) => setEditAllowance((s) => ({ ...s, days: e.target.value }))}
              required
            />
            {/* Always days, even where the organisation shows hours. The stored
                allowance is a number of days; typing it in hours would round
                somebody's entitlement through a division by their shift length. */}
            <p className="text-[12px] text-[#64748B] dark:text-gray-400 -mt-2">
              Entered in days.
              {editAllowance.balance.has_roster ? (
                <>
                  {' '}Their shift is {editAllowance.balance.day_hours}h, so this is{' '}
                  <span className="font-semibold text-gray-700 dark:text-gray-200">
                    {(Number(editAllowance.days || 0) * editAllowance.balance.day_hours).toFixed(2).replace(/\.00$/, '')} hours
                  </span>
                  {' '}of leave.
                </>
              ) : (
                <>
                  {' '}No rota pattern is on file for them, so a day counts as 8 hours until
                  one is set.
                </>
              )}
            </p>

            <FormField
              label="Reason (optional)"
              name="notes"
              value={editAllowance.notes}
              onChange={(e) => setEditAllowance((s) => ({ ...s, notes: e.target.value }))}
              placeholder="e.g. +3 days long service"
            />

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#F1F5F9] dark:border-gray-800">
              <Button type="button" variant="secondary" onClick={() => setEditAllowance(null)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={savingAllowance}>
                {savingAllowance ? 'Saving…' : 'Save allowance'}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default LeaveApprovals;
